import { useQuery } from '@tanstack/react-query';

import { queryKeys } from 'api/config';
import { useQueryHTTPRequest } from 'api/hooks/useHTTPRequest';

import { Participant } from 'features/participants/config/types';
import {
  hasEngagementData,
  normalizeParticipantEngagement,
} from 'features/participants/utils/engagement';
import { ParticipantSprint } from 'features/sprint';

export const useParticipantsQuery = (
  participantsMapper = mapParticipantData
) => {
  const { request } = useQueryHTTPRequest();
  const { data, isFetching, isLoading } = useQuery(
    [queryKeys.participants],
    () => request({ url: `/api/coach/clients` }),

    {
      select: (data) => data?.items.map(participantsMapper),
    }
  );

  return { participants: data || [], isFetching, isLoading };
};

export const mapParticipantData = (participant: Participant) => {
  const {
    userEmail,
    userName: fullName,
    firstName,
    lastName,
    userPicture,
    lastActive,
    latestSprint,
    coachingStatus,
    coachingMode,
    coachedSprintsCount,
    unreadChatMessagesNumber,
    unreadNotesRepliesNumber,
    chatId,
    userId,
    firstUnreadJournalRecordDatetime,
    unreadJournalRecordsNumber,
    timezone,
    nextSessionAppointment,
    engagementDay,
  } = participant;

  const hasSprint = !!latestSprint;
  const mappedSprint = hasSprint && new ParticipantSprint(latestSprint);
  const normalizedEngagement = engagementDay
    ? normalizeParticipantEngagement(engagementDay)
    : undefined;
  const engagementDetails =
    normalizedEngagement && hasEngagementData(normalizedEngagement)
      ? normalizedEngagement
      : undefined;

  return {
    userInfo: {
      email: userEmail,
      picture: userPicture,
      fullName,
      firstName,
      lastName,
      userId,
      timezone,
    },
    sprintDuration: hasSprint ? mappedSprint.duration : undefined,
    latestSprint: mappedSprint,
    lastActive,
    coachingStatus,
    coachingMode,
    coachedSprintsCount: coachedSprintsCount || undefined,
    sprintType: mappedSprint?.sprintTypeLabel,
    currentWeekNumber: hasSprint ? mappedSprint.weekNumberLabel : undefined,
    unreadChatMessagesNumber,
    unreadNotesRepliesNumber,
    nextSessionAppointment: nextSessionAppointment || undefined,
    chatId,
    momentumScores:
      mappedSprint?.momentumScores?.length > 0
        ? mappedSprint.momentumScores
        : undefined,
    journal: {
      firstUnreadJournalRecordDatetime,
      unreadJournalRecordsNumber,
    },
    chat: {
      unreadChatMessagesNumber,
      chatId,
    },
    engagement: engagementDetails,
  };
};
